/**
 * DateRangeSelector — chip-style presets + a custom range popover.
 *
 * Dedrone has: Today, Yesterday, Last 7 days, Last 30 days, This month,
 * Last month, Last 90 days, Custom. We match that list.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  format,
} from 'date-fns';
import clsx from 'clsx';

export interface DateRange {
  from: Date;
  to: Date;
  label: string;
}

const PRESETS: Array<{ key: string; label: string; build: () => DateRange }> = [
  {
    key: 'today',
    label: 'Today',
    build: () => {
      const d = new Date();
      return { from: startOfDay(d), to: endOfDay(d), label: 'Today' };
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    build: () => {
      const d = subDays(new Date(), 1);
      return { from: startOfDay(d), to: endOfDay(d), label: 'Yesterday' };
    },
  },
  {
    key: '7d',
    label: 'Last 7 days',
    build: () => ({
      from: startOfDay(subDays(new Date(), 6)),
      to: endOfDay(new Date()),
      label: 'Last 7 days',
    }),
  },
  {
    key: '30d',
    label: 'Last 30 days',
    build: () => ({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
      label: 'Last 30 days',
    }),
  },
  {
    key: 'thisMonth',
    label: 'This month',
    build: () => {
      const d = new Date();
      return { from: startOfMonth(d), to: endOfDay(d), label: 'This month' };
    },
  },
  {
    key: 'lastMonth',
    label: 'Last month',
    build: () => {
      const d = subMonths(new Date(), 1);
      return { from: startOfMonth(d), to: endOfMonth(d), label: 'Last month' };
    },
  },
  {
    key: '90d',
    label: 'Last 90 days',
    build: () => ({
      from: startOfDay(subDays(new Date(), 89)),
      to: endOfDay(new Date()),
      label: 'Last 90 days',
    }),
  },
];

interface Props {
  value: DateRange;
  onChange: (r: DateRange) => void;
}

// Format a Date as YYYY-MM-DD in LOCAL time for <input type="date">.
// `date.toISOString().slice(0, 10)` would shift in non-UTC zones and give
// the wrong day for users west of GMT around midnight.
function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function DateRangeSelector({ value, onChange }: Props) {
  const displayLabel = useMemo(() => {
    if (value.label) return value.label;
    return `${format(value.from, 'MMM d')} – ${format(value.to, 'MMM d')}`;
  }, [value]);

  // Custom-range editor state. Lives outside the Menu's open state so it
  // persists if the user clicks away mid-edit; resets to current `value`
  // when the menu reopens.
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => toInputDate(value.from));
  const [customTo, setCustomTo] = useState(() => toInputDate(value.to));
  const [customError, setCustomError] = useState<string | null>(null);

  // Sync the editor with the active range whenever it changes from outside
  // (e.g. the user picks a preset).
  useEffect(() => {
    setCustomFrom(toInputDate(value.from));
    setCustomTo(toInputDate(value.to));
  }, [value.from, value.to]);

  const applyCustom = (close: () => void) => {
    // <input type="date"> returns "" for an empty field. Guard against that
    // before parsing — `new Date('')` is Invalid Date.
    if (!customFrom || !customTo) {
      setCustomError('Pick both a start and an end date.');
      return;
    }
    const from = startOfDay(new Date(`${customFrom}T00:00:00`));
    const to = endOfDay(new Date(`${customTo}T00:00:00`));
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setCustomError('Invalid date.');
      return;
    }
    if (from.getTime() > to.getTime()) {
      setCustomError('Start must be before end.');
      return;
    }
    setCustomError(null);
    onChange({
      from,
      to,
      label: `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`,
    });
    setShowCustom(false);
    close();
  };

  return (
    <Menu as="div" className="relative">
      {({ close }) => (
        <>
          <Menu.Button className="btn-secondary flex items-center gap-2 min-w-[180px] justify-between">
            <span className="flex items-center gap-2">
              <CalendarDaysIcon className="h-4 w-4 text-accent" />
              {displayLabel}
            </span>
            <ChevronDownIcon className="h-4 w-4" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={clsx(
                'absolute right-0 mt-2 origin-top-right bg-navy-800 border border-navy-700 rounded-lg shadow-xl z-40 focus:outline-none overflow-hidden',
                // Widen when the custom-range editor is showing — two date
                // inputs side-by-side don't fit in the 14rem preset width.
                showCustom ? 'w-72' : 'w-56',
              )}
            >
              <div className="p-1">
                {PRESETS.map((p) => {
                  const active = value.label === p.label;
                  return (
                    <Menu.Item key={p.key}>
                      {({ active: hovered }) => (
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustom(false);
                            onChange(p.build());
                          }}
                          className={clsx(
                            'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left',
                            hovered ? 'bg-navy-700 text-white' : 'text-gray-300',
                          )}
                        >
                          {p.label}
                          {active && <span className="text-accent">✓</span>}
                        </button>
                      )}
                    </Menu.Item>
                  );
                })}

                {/* Custom range toggle. Not a Menu.Item — clicking it must
                    NOT close the menu, because the editor renders below. */}
                <button
                  type="button"
                  onClick={() => setShowCustom((v) => !v)}
                  className={clsx(
                    'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left mt-1 border-t border-navy-700',
                    showCustom ? 'text-accent' : 'text-gray-300 hover:bg-navy-700 hover:text-white',
                  )}
                >
                  Custom range
                  <span className="text-xs">{showCustom ? '▾' : '▸'}</span>
                </button>

                {showCustom && (
                  <div
                    className="px-3 py-3 space-y-2 border-t border-navy-700"
                    // Ditto — interactions inside this block stay open.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-gray-500 uppercase tracking-wider">
                        From
                      </label>
                      <input
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="bg-navy-900 border border-navy-700 rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-gray-500 uppercase tracking-wider">
                        To
                      </label>
                      <input
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        max={toInputDate(new Date())}
                        className="bg-navy-900 border border-navy-700 rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
                      />
                    </div>
                    {customError && (
                      <div className="text-xs text-rose-400">{customError}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => applyCustom(close)}
                      className="w-full mt-1 px-3 py-1.5 rounded-md bg-accent hover:bg-accent-light text-navy-900 text-sm font-semibold"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

// Default export value — Last 30 days matches the API default and Dedrone's.
export const DEFAULT_RANGE: DateRange = {
  from: startOfDay(subDays(new Date(), 29)),
  to: endOfDay(new Date()),
  label: 'Last 30 days',
};
